import React, { useState, useEffect } from "react";
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiEye } from "react-icons/fi";
import { contactService } from "../services/contactService";
import ContactModal from "./modals/ContactModal";
import ViewContactModal from "./modals/ViewContactModal";
import ConfirmationPopup from "./common/ConfirmationPopup";

const RELATIONSHIP_COLORS = {
  friend: { bg: "rgba(0, 150, 137, 0.15)", color: "var(--teal-bg)" },
  family: { bg: "rgba(156, 39, 176, 0.15)", color: "#9c27b0" },
  colleague: { bg: "rgba(33, 150, 243, 0.15)", color: "#2196f3" },
  client: { bg: "rgba(255, 152, 0, 0.15)", color: "#ff9800" },
  business: { bg: "rgba(76, 175, 80, 0.15)", color: "#4caf50" },
  other: { bg: "rgba(158, 158, 158, 0.15)", color: "#9e9e9e" },
};

const Contacts = ({ userId }) => {
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [deleteContact, setDeleteContact] = useState(null);
  const [viewingContact, setViewingContact] = useState(null);

  useEffect(() => {
    if (userId) {
      loadContacts();
    }
  }, [userId]);

  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = contacts.filter(
        (c) =>
          c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.work_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.personal_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.relationship?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredContacts(filtered);
    } else {
      setFilteredContacts(contacts);
    }
  }, [searchQuery, contacts]);

  const loadContacts = async () => {
    try {
      setIsLoading(true);
      const data = await contactService.getContacts(userId);
      setContacts(data || []);
      setError(null);
    } catch (err) {
      console.error("[Contacts] Load error:", err);
      setError("Failed to load contacts");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddContact = () => {
    setEditingContact(null);
    setIsModalOpen(true);
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (contact) => {
    setDeleteContact(contact);
  };

  const handleViewContact = (contact) => {
    setViewingContact(contact);
  };

  const handleSaveContact = async (formData) => {
    try {
      setIsSaving(true);
      if (editingContact) {
        await contactService.updateContact(userId, editingContact.id, formData);
        setSuccess("Contact updated successfully");
      } else {
        await contactService.createContact(userId, formData);
        setSuccess("Contact added successfully");
      }
      setIsModalOpen(false);
      setEditingContact(null);
      await loadContacts();
    } catch (err) {
      console.error("[Contacts] Save error:", err);
      setError("Failed to save contact");
    } finally {
      setIsSaving(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteContact) return;
    try {
      await contactService.deleteContact(userId, deleteContact.id);
      setSuccess("Contact deleted successfully");
      setDeleteContact(null);
      await loadContacts();
    } catch (err) {
      console.error("[Contacts] Delete error:", err);
      setError("Failed to delete contact");
    }
    setTimeout(() => setSuccess(null), 3000);
  };

  const getRelationshipStyle = (relationship) => {
    const style = RELATIONSHIP_COLORS[relationship?.toLowerCase()] || RELATIONSHIP_COLORS.other;
    return style;
  };

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ").filter((p) => p.length > 0);
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    return parts[0]?.charAt(0).toUpperCase() || "?";
  };

  if (isLoading) {
    return (
      <div style={{ padding: "48px", textAlign: "center" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px", color: "var(--text)" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontWeight: 700, fontSize: "1.4rem" }}>Contacts</h2>
          <p style={{ margin: "4px 0 0", opacity: 0.6, fontSize: "13px" }}>
            {filteredContacts.length} contact{filteredContacts.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button
          onClick={handleAddContact}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "10px 20px",
            borderRadius: "10px",
            border: "none",
            background: "linear-gradient(135deg, var(--teal-bg), #00b3a4)",
            color: "white",
            fontSize: "14px",
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(0, 150, 137, 0.3)",
          }}
        >
          <FiPlus size={18} />
          Add Contact
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div
          style={{
            background: "rgba(231, 0, 11, 0.1)",
            border: "1px solid var(--danger)",
            borderRadius: "10px",
            padding: "12px 16px",
            marginBottom: "16px",
            color: "var(--danger)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <i className="bi bi-exclamation-circle-fill"></i>
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            background: "rgba(0, 150, 137, 0.1)",
            border: "1px solid var(--teal-bg)",
            borderRadius: "10px",
            padding: "12px 16px",
            marginBottom: "16px",
            color: "var(--teal-bg)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <i className="bi bi-check-circle-fill"></i>
          {success}
        </div>
      )}

      {/* Search */}
      <div style={{ position: "relative", marginBottom: "24px" }}>
        <FiSearch
          style={{
            position: "absolute",
            left: "14px",
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--teal-bg)",
            fontSize: "18px",
          }}
        />
        <input
          type="text"
          placeholder="Search contacts by name, email, or relationship..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: "100%",
            padding: "12px 16px 12px 44px",
            borderRadius: "12px",
            border: "1px solid var(--border-color)",
            background: "var(--input-box-bg)",
            color: "var(--text)",
            fontSize: "14px",
          }}
        />
      </div>

      {/* Contacts List */}
      {filteredContacts.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "48px 24px",
            background: "var(--input-box-bg)",
            borderRadius: "16px",
            border: "1px solid var(--border-color)",
          }}
        >
          <i
            className="bi bi-people"
            style={{ fontSize: "48px", color: "var(--teal-bg)", opacity: 0.5 }}
          ></i>
          <h4 style={{ marginTop: "16px", fontWeight: 600 }}>
            {searchQuery ? "No contacts found" : "No contacts yet"}
          </h4>
          <p style={{ opacity: 0.6, margin: "8px 0 0" }}>
            {searchQuery
              ? "Try a different search term"
              : "Click 'Add Contact' to create your first contact"}
          </p>
        </div>
      ) : (
        <div
          style={{
            background: "var(--input-box-bg)",
            borderRadius: "14px",
            border: "1px solid var(--border-color)",
            overflow: "hidden",
          }}
        >
          {/* Table Header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr 2fr 1.5fr 140px",
              gap: "16px",
              padding: "14px 20px",
              background: "var(--background)",
              borderBottom: "1px solid var(--border-color)",
              fontSize: "12px",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              opacity: 0.7,
            }}
          >
            <div>Name</div>
            <div>Relationship</div>
            <div>Email</div>
            <div>Phone</div>
            <div style={{ textAlign: "center" }}>Actions</div>
          </div>

          {/* Table Rows */}
          {filteredContacts.map((contact, index) => {
            const relStyle = getRelationshipStyle(contact.relationship);
            return (
              <div
                key={contact.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 2fr 1.5fr 140px",
                  gap: "16px",
                  padding: "14px 20px",
                  alignItems: "center",
                  borderBottom: index < filteredContacts.length - 1 ? "1px solid var(--border-color)" : "none",
                  transition: "background 0.15s ease",
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "rgba(0, 150, 137, 0.04)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
              >
                {/* Name with Avatar */}
                <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, var(--teal-bg), #00b3a4)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontWeight: 700,
                      fontSize: "13px",
                      flexShrink: 0,
                    }}
                  >
                    {getInitials(contact.name)}
                  </div>
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: "14px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {contact.name}
                  </span>
                </div>

                {/* Relationship */}
                <div>
                  {contact.relationship ? (
                    <span
                      style={{
                        display: "inline-block",
                        padding: "4px 10px",
                        borderRadius: "20px",
                        fontSize: "11px",
                        fontWeight: 600,
                        textTransform: "capitalize",
                        background: relStyle.bg,
                        color: relStyle.color,
                      }}
                    >
                      {contact.relationship}
                    </span>
                  ) : (
                    <span style={{ opacity: 0.4, fontSize: "13px" }}>-</span>
                  )}
                </div>

                {/* Email */}
                <div
                  style={{
                    fontSize: "13px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    opacity: 0.85,
                  }}
                >
                  {contact.work_email || contact.personal_email || (
                    <span style={{ opacity: 0.4 }}>-</span>
                  )}
                </div>

                {/* Phone */}
                <div
                  style={{
                    fontSize: "13px",
                    opacity: 0.85,
                  }}
                >
                  {contact.work_phone || contact.personal_phone || (
                    <span style={{ opacity: 0.4 }}>-</span>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: "flex", justifyContent: "center", gap: "6px" }}>
                  <button
                    onClick={() => handleViewContact(contact)}
                    title="View"
                    style={{
                      padding: "6px 8px",
                      borderRadius: "6px",
                      border: "1px solid var(--border-color)",
                      background: "var(--background)",
                      color: "var(--teal-bg)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FiEye size={14} />
                  </button>
                  <button
                    onClick={() => handleEditContact(contact)}
                    title="Edit"
                    style={{
                      padding: "6px 8px",
                      borderRadius: "6px",
                      border: "1px solid var(--border-color)",
                      background: "var(--background)",
                      color: "var(--text)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FiEdit2 size={14} />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(contact)}
                    title="Delete"
                    style={{
                      padding: "6px 8px",
                      borderRadius: "6px",
                      border: "1px solid rgba(231, 0, 11, 0.3)",
                      background: "rgba(231, 0, 11, 0.08)",
                      color: "var(--danger)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FiTrash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Contact Modal */}
      <ContactModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingContact(null);
        }}
        onSave={handleSaveContact}
        contact={editingContact}
        isLoading={isSaving}
      />

      {/* Delete Confirmation */}
      <ConfirmationPopup
        show={!!deleteContact}
        handleClose={() => setDeleteContact(null)}
        handleConfirm={handleConfirmDelete}
        title="Delete Contact?"
        body={`Are you sure you want to delete "${deleteContact?.name}"? This action cannot be undone.`}
      />

      {/* View Contact Modal */}
      <ViewContactModal
        isOpen={!!viewingContact}
        onClose={() => setViewingContact(null)}
        contact={viewingContact}
      />
    </div>
  );
};

export default Contacts;
